import { depictMerchant } from "@/lib/depict-client/depict-merchant"
import { type IListingService } from "@/lib/depict-client/interfaces/IListingService"
import { type GetListingRequest } from "@/lib/depict-client/types/api/GetListingRequest"
import { type GetListingResponse } from "@/lib/depict-client/types/api/GetListingResponse"
import { type ListListingsRequest } from "@/lib/depict-client/types/api/ListListingsRequest"
import { type ListListingsResponseItem } from "@/lib/depict-client/types/api/ListListingsResponseItem"
import { type ProductListingRequestV3 } from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { type ProductListingResponseV3 } from "@/lib/depict-client/types/api/ProductListingResponseV3"
import { type ClientFetchService } from "@/lib/http/ClientFetchService"
import { type FetchService } from "@/lib/http/FetchService"
import { type Result } from "@/types/generic/Result"
import { stringify } from "neoqs"

export class ListingApiService implements IListingService {
    constructor(private fetchService: FetchService | ClientFetchService) {}

    /**
     * Returns the full listing hierarchy. Useful for navigation menus, listing selection dialogs, etc.
     * Can optionally be filtered to only show listings of certain types.
     * @param {ListListingsRequest} req
     * @link https://docs.depict.ai/reference/openapi-reference/listing-v3/get-listings
     */
    public getListings = async (
        req: ListListingsRequest
    ): Promise<Result<ListListingsResponseItem[]>> => {
        return this.fetchService.fetch(
            "/listings?" + stringify({ ...req, merchant: depictMerchant }),
            {
                method: "GET"
            }
        )
    }

    /**
     * Returns a single listing.
     * @param {string} external_id
     * @param {GetListingRequest} req
     * @link https://docs.depict.ai/reference/openapi-reference/listing-v3/get-listing-via-external-id
     */
    public getListingViaExternalId = async (
        external_id: string,
        req: GetListingRequest
    ): Promise<Result<GetListingResponse>> => {
        return this.fetchService.fetch(
            `listings/external_id/${external_id}?` +
                stringify({ ...req, merchant: depictMerchant }),
            {
                method: "GET"
            }
        )
    }

    /**
     * Returns a single listing.
     * @param {string} listing_id
     * @param {GetListingRequest} req
     * @link https://docs.depict.ai/reference/openapi-reference/listing-v3/get-listing
     */
    public getListing = async (
        listing_id: string,
        req: GetListingRequest
    ): Promise<Result<GetListingResponse>> => {
        return this.fetchService.fetch(
            `listings/${listing_id}?` + stringify({ ...req, merchant: depictMerchant }),
            {
                method: "GET"
            }
        )
    }

    /**
     * Returns products in a listing, optionally filtered. Used for product listing pages.
     * @param {string} external_id
     * @param {ProductListingRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/listing-v3/get-products-in-listing-via-external-id
     */
    public getProductsInListingViaExternalId = async (
        external_id: string,
        req: ProductListingRequestV3
    ): Promise<Result<ProductListingResponseV3>> => {
        return this.fetchService.fetch(`listings/external_id/${external_id}/products`, {
            method: "POST",
            body: JSON.stringify({ ...req, merchant: depictMerchant })
        })
    }

    /**
     * Returns products in a listing, optionally filtered. Used for product listing pages.
     * @param {string} listing_id
     * @param {ProductListingRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/listing-v3/get-products-in-listing
     */
    public getProductsInListing = async (
        listing_id: string,
        req: ProductListingRequestV3
    ): Promise<Result<ProductListingResponseV3>> => {
        // @todo: we also can may external_id in the query.
        return this.fetchService.fetch(`listings/${listing_id}/products`, {
            method: "POST",
            body: JSON.stringify({ ...req, merchant: depictMerchant })
        })
    }
}
